import * as Sentry from '@sentry/react';

// Injected by vite.config.ts `define:` — stamped at build time with
// the git SHA / Railway deploy id. Sentry stitches this to the
// uploaded source maps so minified stacktraces resolve to source.
declare const __SENTRY_RELEASE__: string;
const release = typeof __SENTRY_RELEASE__ !== 'undefined' ? __SENTRY_RELEASE__ : 'dev';

/**
 * URLs we extend distributed tracing into. When the user's browser
 * hits one of these, Sentry propagates a `sentry-trace` header so
 * the backend's Sentry SDK can link its spans to the frontend
 * transaction that triggered them. Without the regex match, traces
 * stay siloed on each side.
 */
const TRACE_PROPAGATION_TARGETS = [
  'localhost',
  /^\//, // every same-origin request
  /^https:\/\/altnapp-production\.up\.railway\.app\/api/,
  /^https:\/\/pacioli\.altailabs\.io\/api/,
  /^https:\/\/.*\.altailabs\.io/,
];

export function initErrorTracking() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    release,

    // Automatic IP + user-agent enrichment on captured events.
    // Processed under the Privacy Policy at altailabs.io/#/docs.
    sendDefaultPii: true,

    // Performance transactions + distributed tracing. Without this
    // integration, `tracesSampleRate` is a no-op — we were silently
    // shipping zero traces before this was added.
    integrations: [
      Sentry.browserTracingIntegration(),
      // Session replay — records DOM + network + console for the
      // 15s window around any captured error so Sentry can replay
      // what the user saw. `replaysOnErrorSampleRate: 1.0` below
      // means every error gets a replay.
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
        maskAllInputs: true, // keep passwords / tokens / forms masked
      }),
      // User Feedback — autoInject OFF so Sentry's widget doesn't
      // stack alongside our own <FeedbackButton>. The FeedbackButton
      // dual-writes: POSTs to /api/v1/feedback (→ Telegram relay
      // for ops visibility) AND calls Sentry.captureFeedback (→
      // linked to the current trace + attached replay so support
      // sees the exact session the user was in when they typed).
      Sentry.feedbackIntegration({
        colorScheme: 'system',
        autoInject: false,
      }),
    ],

    // Capture 100% in non-prod, 20% in prod (cheap enough for our
    // traffic volume, dial down later if the quota bites). We have
    // per-backend-route sampling on the API too.
    tracesSampleRate: import.meta.env.MODE === 'production' ? 0.2 : 1.0,
    tracePropagationTargets: TRACE_PROPAGATION_TARGETS,

    // Replay sampling — 10% of regular sessions in prod (per
    // Sentry's recommended baseline), 100% when an error happens so
    // we always capture the user's path into the failure. Bump to
    // 1.0 for `session` in non-prod so every dev session is replayable.
    replaysSessionSampleRate: import.meta.env.MODE === 'production' ? 0.1 : 1.0,
    replaysOnErrorSampleRate: 1.0,
  });
}

export function captureException(err: unknown, context?: Record<string, unknown>) {
  if (!import.meta.env.VITE_SENTRY_DSN) return;
  Sentry.captureException(err, { extra: context });
}
